import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, ExternalLink } from 'lucide-react';
import Logo from '../common/Logo';
import { subscribeToNewsletter } from '../../services/domain/subscribeService';
import { ROUTES } from '../../constants/routes';
import { useLanguage } from '../../contexts/LanguageContext';

const Footer: React.FC = () => {
  const [footerEmail, setFooterEmail] = useState('');
  const [footerSubscribeStatus, setFooterSubscribeStatus] = useState('idle');
  const [footerMessage, setFooterMessage] = useState('');
  const { t } = useLanguage(); // Add the translation function

  const handleFooterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!footerEmail || !footerEmail.includes('@')) {
      setFooterMessage(t('enter_valid_email'));
      return;
    }
    
    setFooterSubscribeStatus('loading');
    
    try {
      const result = await subscribeToNewsletter(footerEmail);
      
      if (result.success) {
        setFooterSubscribeStatus('success');
        setFooterMessage(t('subscribed_successfully'));
        setFooterEmail('');
      } else {
        setFooterSubscribeStatus('error');
        setFooterMessage(result.message);
      }
      
      // Reset after 3 seconds
      setTimeout(() => {
        setFooterSubscribeStatus('idle');
        setFooterMessage('');
      }, 3000);
    } catch (error) {
      setFooterSubscribeStatus('error');
      setFooterMessage(t('failed_to_subscribe'));
      
      setTimeout(() => {
        setFooterSubscribeStatus('idle');
        setFooterMessage('');
      }, 3000);
    }
  };

  return (
    <footer className="bg-neutral-900 text-white pt-12 pb-6">
      <div className="container-custom">
        {/* Highlighted Advertise Link Banner - Added at the top of the footer */}
        <div className="bg-primary-600 rounded-lg p-4 mb-8 flex flex-col md:flex-row items-center justify-between">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <h4 className="text-white font-serif text-lg font-bold">{t('advertise_with_us')}</h4>
            <p className="text-white/90 text-sm mt-1">
              {t('reach_legal_audience')}
            </p>
          </div>
          <Link 
            to={ROUTES.ADVERTISE || "/advertise"}
            className="btn bg-white text-primary-600 hover:bg-neutral-100 px-6 py-2 rounded-md font-medium flex items-center"
          >
            {t('learn_more')}
            <ExternalLink size={16} className="ml-2" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="mb-4">
              <Logo variant="light" />
            </div>
            <p className="text-neutral-300 mb-4">
              {t('footer_description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com" className="text-neutral-300 hover:text-white transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://twitter.com" className="text-neutral-300 hover:text-white transition-colors" aria-label="Twitter">
                <Twitter size={20} />
              </a>
              <a href="https://instagram.com" className="text-neutral-300 hover:text-white transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://linkedin.com" className="text-neutral-300 hover:text-white transition-colors" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-white font-serif text-lg mb-4">{t('resources')}</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={ROUTES.CATEGORY_BY_SLUG('supreme-court')} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('supreme_court')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.CATEGORY_BY_SLUG('high-courts')}
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('high_courts')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.BARE_ACTS} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('bare_acts')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.LAW_FIRMS} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('law_firms')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.CATEGORY_BY_SLUG('top-stories')} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('top_stories')}
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-serif text-lg mb-4">{t('company')}</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={ROUTES.ABOUT || "/about"} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('about_us')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.CONTACT || "/contact"} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('contact_us')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy-policy" 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('privacy_policy')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms-of-use" 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('terms_of_use')}
                </Link>
              </li>
              <li>
                <Link 
                  to={ROUTES.SUBSCRIBE || "/subscribe-premium"} 
                  className="text-neutral-300 hover:text-white transition-colors"
                >
                  {t('subscribe')}
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-serif text-lg mb-4">{t('newsletter')}</h4>
            <p className="text-neutral-300 mb-4">
              {t('newsletter_description')}
            </p>
            <form className="space-y-3" onSubmit={handleFooterSubscribe}>
              <div>
                <input
                  type="email"
                  placeholder={t('your_email_address')}
                  value={footerEmail}
                  onChange={(e) => setFooterEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  disabled={footerSubscribeStatus === 'loading'}
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary w-full flex items-center justify-center"
                disabled={footerSubscribeStatus === 'loading'}
              >
                {footerSubscribeStatus === 'loading' ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                ) : (
                  <Mail size={16} className="mr-2" />
                )}
                {t('subscribe')}
              </button>
              {footerMessage && (
                <p className={`text-sm ${
                  footerSubscribeStatus === 'success' ? 'text-green-400' : 
                  footerSubscribeStatus === 'error' ? 'text-red-400' : ''
                }`}>
                  {footerMessage}
                </p>
              )}
            </form>
          </div>
        </div>
        
        <div className="border-t border-neutral-800 pt-6 text-center text-neutral-400 text-sm">
          <p>© {new Date().getFullYear()} {t('legal_nest')}. {t('all_rights_reserved')}.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;